import type { PageAdOptions, PageAdSize, PageAdType } from '@/modules/adv'
import { Banner } from '@/modules/adv/banner'
import { AdProviderNames } from '@/types'
import type { DirectAdEvent, EventForLoggersType } from '@/types/logger.types'
import { isElementViewable } from '@/utils/is-element-viewable'

import type { Creative } from './types'

function getImageUrl(creative: Creative): string {
    return `https://playgama.com/cdn-cgi/imagedelivery/LN2S-4p3-GgZvEx3IPaKUA/${creative.file}/original`
}

const MICRO_TIMEOUT = 16

function clearElementAttributes(element: HTMLElement): void {
    for (const attr of Array.from(element.attributes)) {
        if (attr.name !== 'id' && attr.name !== 'class') {
            element.removeAttribute(attr.name)
        }
    }
}

export class ImageBanner {
    private readonly logEvent: (event: EventForLoggersType) => void
    private readonly getAdUnit: (type: PageAdType, creative?: Creative) => string
    private readonly sendRequest: (url: string | undefined) => void
    private readonly getCreative: (type: PageAdType, sizes?: PageAdSize[]) => Creative | null

    private intersectionObserver: IntersectionObserver
    private mutationObserver: MutationObserver
    private adTrackingData: Map<string, { creative: Creative; type: PageAdType }> = new Map()

    constructor({
        logEvent,
        getAdUnit,
        sendRequest,
        getCreative,
    }: {
        logEvent: (event: EventForLoggersType) => void
        getAdUnit: (type: PageAdType, creative?: Creative) => string
        sendRequest: (url: string | undefined) => void
        getCreative: (type: PageAdType, sizes?: PageAdSize[]) => Creative | null
    }) {
        this.logEvent = logEvent
        this.getAdUnit = getAdUnit
        this.sendRequest = sendRequest
        this.getCreative = getCreative

        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const adId = entry.target.id
                        const trackingData = this.adTrackingData.get(adId)

                        if (trackingData) {
                            this.log(trackingData.type, 'direct_ad_impression', trackingData.creative)
                            this.sendRequest(trackingData.creative.impressionTrackingUrl)
                            this.intersectionObserver.unobserve(entry.target)
                            this.adTrackingData.delete(adId)
                        }
                    }
                })
            },
            {
                threshold: 0.5, // Fire when 50% of the ad is visible
                rootMargin: '0px',
            },
        )

        this.mutationObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes') {
                    clearElementAttributes(mutation.target as HTMLElement)
                }
            }
        })
    }

    private createAdElement(adId: string, creative: Creative): HTMLImageElement {
        const adElement = document.createElement('img')
        adElement.id = adId
        adElement.src = getImageUrl(creative)

        if (creative.width && creative.height) {
            adElement.width = creative.width
            adElement.height = creative.height
        }

        this.intersectionObserver.observe(adElement)

        return adElement
    }

    private log(type: PageAdType, event: DirectAdEvent, creative?: Creative): void {
        this.logEvent({
            event: 'direct_ad_event',
            eventName: event,
            adPlacement: type,
            adUnit: this.getAdUnit(type, creative),
        })
    }

    requestAd(options: PageAdOptions): Promise<Banner> {
        const { type, el, sizes, refresh } = options

        let timerId: number | undefined

        const adId = crypto.randomUUID()
        const link = document.createElement('a')

        this.log(type, 'direct_ad_request')

        const element = el && (document.querySelector(`#${el}`) as HTMLElement)

        const banner = new Banner({
            provider: AdProviderNames.DIRECT,
            adUnit: this.getAdUnit(type),
            destroy: () => {
                this.adTrackingData.delete(adId)
                link.remove()

                if (timerId) {
                    window.clearTimeout(timerId)
                }
            },
        })

        if (refresh) {
            timerId = window.setTimeout(async () => {
                const shouldRefresh = !!el && (await isElementViewable(el))

                if (shouldRefresh) {
                    this.adTrackingData.delete(adId)
                    link.remove()
                    this.requestAd(options)
                }
            }, refresh * 1000)
        }

        const creative = this.getCreative(type, sizes)

        if (!element || !creative || creative.format !== 'banner') {
            window.setTimeout(() => {
                banner.triggerEmpty()
            }, MICRO_TIMEOUT)
            return Promise.resolve(banner)
        }

        clearElementAttributes(element)
        this.mutationObserver.observe(element, {
            attributes: true,
        })

        this.adTrackingData.set(adId, { creative, type })

        link.style.display = 'flex'
        link.style.justifyContent = 'center'

        link.href = `/redirect.html?url=${encodeURIComponent(creative.clickUrl)}`
        link.target = '_blank'

        link.addEventListener('click', () => {
            this.log(type, 'direct_ad_click', creative)
        })

        link.appendChild(this.createAdElement(adId, creative))
        element.appendChild(link)

        window.setTimeout(() => {
            banner.triggerRendered()
        }, MICRO_TIMEOUT)

        window.setTimeout(() => {
            banner.triggerViewable()
        }, MICRO_TIMEOUT * 2)

        return Promise.resolve(banner)
    }
}
